/**
 * The deck's action area is lent to it by exactly ONE card at a time, and this is the cell that
 * records which. It is deliberately tiny and pure so the invariant can be stated and tested away
 * from React's effect scheduling — which is what the bug it exists to prevent depended on.
 *
 * `T` is the controls payload; this module only cares about who owns it.
 */
export type LearnCardControlsClaim<T> = { controls: T; presentationKey: string } | null;

/**
 * Apply one claim or release.
 *
 * Two cards are mounted across a promotion, and React does not order the outgoing card's effect
 * cleanup against the incoming card's effect. If the cleanup runs last and releases
 * unconditionally, it wipes the controls the arriving card just installed — and nothing
 * re-registers, because the arriving card's effect has already run. The deck is then stranded:
 * a fully rendered card whose Reveal and rating buttons do nothing, since every commit path bails
 * on absent controls.
 *
 * So a release only ever retracts the claim of the presentation that made it. A late release from
 * a card that has already been superseded is identifiable, and is dropped.
 */
export function applyLearnCardControlsClaim<T>(
  claim: LearnCardControlsClaim<T>,
  presentationKey: string,
  controls: T | null,
): LearnCardControlsClaim<T> {
  if (controls !== null) {
    return { controls, presentationKey };
  }
  return claim === null || claim.presentationKey === presentationKey ? null : claim;
}

/** The controls to show for `presentationKey` — never another presentation's. */
export function learnCardControlsForPresentation<T>(
  claim: LearnCardControlsClaim<T>,
  presentationKey: string,
): T | null {
  return claim !== null && claim.presentationKey === presentationKey ? claim.controls : null;
}
